/**
 * Pure mappings for the study-centre dashboard charts.
 *
 * Floor numbers come from one `GET /api/bookings/dashboard-stats` call;
 * learner and group charts read `/api/students/learning-summary` and
 * `/api/study-groups`. Nothing here invents a number the payload did
 * not carry.
 */

export const SUMMARY_PAGE_LIMIT = 200;
export const TREND_DAYS = 14;

const NAMED_SHIFTS: Record<string, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};

export function shortDayLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return dateKey;
  const date = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  }).format(date);
}

/** Opaque slot keys stay readable. Unknown keys pass through. */
export function shiftLabel(slotKey: string): string {
  const named = NAMED_SHIFTS[slotKey];
  if (named) return named;
  const ranged = /^slot-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/.exec(slotKey);
  if (!ranged) return slotKey;
  return `${clock(ranged[1]!, ranged[2]!)}–${clock(ranged[3]!, ranged[4]!)}`;
}

function clock(hours: string, minutes: string): string {
  const hour = Number(hours);
  const suffix = hour >= 12 ? "PM" : "AM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return minutes === "00" ? `${twelve} ${suffix}` : `${twelve}:${minutes} ${suffix}`;
}

export interface OccupancySegment {
  name: string;
  value: number;
  color: string;
}

// One tenant-themed hue at three strengths: the seat in use reads strongest,
// a held seat next, free capacity faintest. `--color-secondary` is a surface
// tone in this theme and disappears against the card.
const COLOR_CHECKED_IN = "var(--color-primary)";
const COLOR_BOOKED = "color-mix(in oklch, var(--color-primary) 55%, var(--color-card))";
const COLOR_FREE = "color-mix(in oklch, var(--color-primary) 16%, var(--color-muted))";
const COLOR_SERIES = "var(--color-primary)";

export function occupancySegments(input: {
  held: number;
  checkedIn: number;
  freeSeats: number | null;
}): OccupancySegment[] {
  const segments: OccupancySegment[] = [
    { name: "Booked", value: input.held, color: COLOR_BOOKED },
    { name: "Checked in", value: input.checkedIn, color: COLOR_CHECKED_IN },
  ];
  if (input.freeSeats != null) {
    segments.push({ name: "Free", value: input.freeSeats, color: COLOR_FREE });
  }
  return segments.filter((segment) => segment.value > 0);
}

/** Response of `GET /api/bookings/dashboard-stats`. */
export interface BookingDashboardStats {
  date_key: string;
  seats_booked: number;
  seats_checked_in: number;
  active_seats: number | null;
  by_shift: Array<{ slot_key: string; bookings: number }>;
  trend: Array<{ date_key: string; bookings: number }>;
}

export type ShiftBar = {
  shift: string;
  bookings: number;
};

export type TrendPoint = {
  day: string;
  bookings: number;
};

export interface SeatingFloor {
  occupancy: OccupancySegment[];
  occupancySummary: string;
  occupancyCaption: string | null;
  shifts: ShiftBar[];
  trend: TrendPoint[];
}

/**
 * Seat counts are distinct seats, so booked + checked in + free
 * partitions the active inventory. Free is omitted when the caller
 * cannot see seat inventory.
 */
export function seatingFloorFromStats(stats: BookingDashboardStats): SeatingFloor {
  const freeSeats =
    stats.active_seats == null
      ? null
      : Math.max(0, stats.active_seats - stats.seats_booked - stats.seats_checked_in);
  const occupancy = occupancySegments({
    held: stats.seats_booked,
    checkedIn: stats.seats_checked_in,
    freeSeats,
  });
  const shiftCounts = new Map<string, number>();
  for (const row of stats.by_shift) {
    const label = shiftLabel(row.slot_key);
    shiftCounts.set(label, (shiftCounts.get(label) ?? 0) + row.bookings);
  }
  return {
    occupancy,
    occupancySummary:
      occupancy.map((segment) => `${segment.name} ${segment.value}`).join(", ") ||
      "No seats occupied today",
    occupancyCaption:
      stats.active_seats == null
        ? "Booked and checked-in counts are distinct seats. Free seats need zone access, which this role does not have."
        : null,
    shifts: [...shiftCounts.entries()].map(([shift, bookings]) => ({ shift, bookings })),
    trend: stats.trend.map((row) => ({
      day: shortDayLabel(row.date_key),
      bookings: row.bookings,
    })),
  };
}

export function trendIsEmpty(points: TrendPoint[]): boolean {
  return points.every((point) => point.bookings === 0);
}

export type LearnerActivityRow = {
  metric: string;
  learners: number;
};

export interface LearnerSummaryRow {
  has_portal_login: boolean;
  active_plans: number;
  due_cards: number;
  streak_days: number;
}

export function learnerActivity(items: LearnerSummaryRow[]): LearnerActivityRow[] {
  return [
    { metric: "Active plans", learners: items.filter((item) => item.active_plans > 0).length },
    { metric: "Cards due", learners: items.filter((item) => item.due_cards > 0).length },
    { metric: "On a streak", learners: items.filter((item) => item.streak_days > 0).length },
  ];
}

export type StudyGroupBar = {
  group: string;
  members: number;
};

export function studyGroupBars(
  groups: Array<{ name: string; member_count: number }>,
): StudyGroupBar[] {
  return groups.map((group) => ({
    group: group.name.length > 14 ? `${group.name.slice(0, 13)}…` : group.name,
    members: group.member_count,
  }));
}

export const CHART_SERIES_COLOR = COLOR_SERIES;
